import type { FC, ReactNode } from "react";
import PropTypes from "prop-types";
import { matchPath } from "react-router-dom";
import { List, ListSubheader, useTheme } from "@mui/material";
import type { ListProps } from "@mui/material";
import NavItem from "./NavItem";
import { getCurrentUserRoles } from "src/utils/jwt-helper";

interface Item {
	path?: string;
	icon?: ReactNode;
	info?: ReactNode;
	children?: Item[];
	title: string;
	roles?: string[];
	sx?: any;
	testId?: string;
}

interface NavSectionProps extends ListProps {
	items: Item[];
	pathname: string;
	title: string;
	open?: boolean;
	onClick?: () => void;
}

const renderNavItems = ({
	depth = 0,
	items,
	pathname,
	open,
	onClick,
}: {
	items: Item[];
	pathname: string;
	depth?: number;
	open?: boolean;
	onClick?: () => void;
}): JSX.Element => (
	<List disablePadding>
		{items.reduce(
			// eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
			(acc, item) =>
				reduceChildRoutes({
					acc,
					item,
					pathname,
					depth,
					open,
					onClick,
				}),
			[]
		)}
	</List>
);

const reduceChildRoutes = ({
	acc,
	pathname,
	item,
	depth,
	open,
	onClick,
}: {
	acc: JSX.Element[];
	pathname: string;
	item: Item;
	depth: number;
	open?: boolean;
	onClick?: () => void;
}): Array<JSX.Element> => {
	const key = `${item.title}-${depth}`;
	const exactMatch = item.path
		? !!matchPath(
				{
					path: item.path,
					end: true,
				},
				pathname
		  )
		: false;

	const currentUserRoles = getCurrentUserRoles();
	item.children = item.children?.filter((c) =>
		currentUserRoles.some((r) => c.roles?.includes(r))
	);

	if (item.children) {
		const partialMatch = item.path
			? !!matchPath(
					{
						path: item.path,
						end: false,
					},
					pathname
			  )
			: false;

		acc.push(
			<NavItem
				active={partialMatch}
				depth={depth}
				icon={item.icon}
				info={item.info}
				key={key}
				open={open}
				path={item.path}
				title={item.title}
				onClick={onClick}
				sx={item.sx}
				testId={item.testId}
			>
				{renderNavItems({
					depth: depth + 1,
					items: item.children,
					pathname,
				})}
			</NavItem>
		);
	} else {
		acc.push(
			<NavItem
				active={exactMatch}
				depth={depth}
				icon={item.icon}
				info={item.info}
				open={open}
				key={key}
				path={item.path}
				title={item.title}
				onClick={onClick}
				sx={item.sx}
				testId={item.testId}
			/>
		);
	}

	return acc;
};

const NavSection: FC<NavSectionProps> = (props) => {
	const { items, pathname, title, open, onClick, ...other } = props;

	const theme = useTheme();

	return (
		<List
			subheader={
				<ListSubheader
					disableGutters
					disableSticky
					sx={{
						color: theme.palette.text.primary,
						fontSize: "0.75rem",
						lineHeight: 2.5,
						fontWeight: 700,
						textTransform: "uppercase",
					}}
				>
					{title}
				</ListSubheader>
			}
			{...other}
		>
			{renderNavItems({
				items,
				pathname,
				open,
				onClick,
			})}
		</List>
	);
};

NavSection.propTypes = {
	items: PropTypes.array,
	pathname: PropTypes.string,
	title: PropTypes.string,
};

export default NavSection;
